import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ContentCard from '@/components/feature/ContentCard';
import { useFavorites } from '@/hooks/useFavorites';
import { useSeo } from '@/hooks/useSeo';
import { getContentById, getRelated } from '@/lib/contentService';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = getContentById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useSeo({
    title: item ? `${item.title} - 详情 - 玩偶姐姐` : '内容未找到 - 玩偶姐姐影视内容平台',
    description: item ? item.summary : '该内容可能已下线，请返回首页浏览其他影视作品与资讯。',
    keywords: item
      ? [item.title, ...item.genre, '玩偶姐姐'].join(',')
      : '玩偶姐姐,影视内容',
  });

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!item) {
    return (
      <div className="flex w-full flex-col items-center px-4 py-24">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-[30px]"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">内容不存在或已下线</h1>
        <p className="mt-2 text-sm text-foreground-600">链接可能有误，你可以回到首页继续浏览推荐内容。</p>
        <Link
          to="/"
          className="mt-6 cursor-pointer rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelated(item, 4);
  const favored = isFavorite(item.id);
  const categoryLink = item.type === 'news' ? '/news' : item.type === 'tvshow' ? '/tvshows' : '/movies';
  const categoryLabel = item.type === 'news' ? '影视资讯' : item.type === 'tvshow' ? '电视剧' : '电影';

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast('链接已复制，可直接粘贴分享');
    } catch {
      setToast('复制失败，请从地址栏手动复制链接');
    }
  };

  const handleFavorite = () => {
    const added = toggleFavorite(item.id);
    setToast(added ? '已加入我的收藏' : '已取消收藏');
  };

  const paragraphs = item.type === 'news' ? item.content ?? [] : item.synopsis ?? [];

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100 px-4 py-4 md:px-8">
        <Breadcrumbs items={[{ label: categoryLabel, to: categoryLink }, { label: item.title }]} />
      </div>

      <article className="w-full px-4 py-7 md:px-8 md:py-10">
        <div className={`grid grid-cols-1 gap-6 ${item.type === 'news' ? '' : 'lg:grid-cols-[300px_1fr] lg:gap-10'}`}>
          <div className={item.type === 'news' ? 'hidden' : ''}>
            <div className="relative mx-auto w-full max-w-[240px] overflow-hidden rounded-lg border border-background-200 bg-background-200 lg:max-w-none">
              <div className={item.type === 'news' ? 'aspect-[16/10]' : 'aspect-[2/3]'}>
                <img
                  src={item.cover}
                  alt={`${item.title} 主视觉`}
                  title={`${item.title} ${categoryLabel}`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                  favored
                    ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                    : 'border border-background-300 bg-background-50 text-foreground-800 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                <i className={favored ? 'ri-heart-fill text-[16px]' : 'ri-heart-line text-[16px]'}></i>
                {favored ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-share-forward-line text-[16px]"></i>
                分享
              </button>
            </div>
            <p className="mt-2.5 text-center font-label text-[11px] leading-relaxed text-foreground-400">
              收藏保存在本机浏览器，换设备不会同步
            </p>
          </div>

          <div className="min-w-0">
            {item.type === 'news' && (
              <div className="mb-5 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
                <div className="aspect-[16/9]">
                  <img
                    src={item.cover}
                    alt={`${item.title} 主视觉`}
                    title={`${item.title} 影视资讯`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={categoryLink}
                className="cursor-pointer rounded-sm bg-primary-100 px-2 py-0.5 font-label text-[11px] font-medium text-primary-800 transition-colors hover:bg-primary-200"
              >
                {categoryLabel}
              </Link>
              {item.genre.map((genre) => (
                <span
                  key={genre}
                  className="rounded-sm bg-secondary-100 px-2 py-0.5 font-label text-[11px] text-secondary-800"
                >
                  {genre}
                </span>
              ))}
              {item.status && (
                <span
                  className={`rounded-sm px-2 py-0.5 font-label text-[11px] font-semibold ${
                    item.status === '连载'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-foreground-800 text-background-50'
                  }`}
                >
                  {item.status}
                </span>
              )}
            </div>

            <h1 className="mt-3 font-heading text-2xl font-bold leading-tight tracking-tight text-foreground-950 md:text-[34px]">
              {item.title}
            </h1>
            {item.subtitle && (
              <p className="mt-2 text-sm text-foreground-600 md:text-base">{item.subtitle}</p>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-3 font-label text-xs text-foreground-600">
              {item.type === 'news' ? (
                <>
                  <span>{item.date}</span>
                  <span>文 / {item.author}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line text-[13px]"></i>
                    {(item.heat ?? 60) * 132} 次阅读
                  </span>
                </>
              ) : (
                <>
                  <span>{item.year} 年</span>
                  <span>{item.region}</span>
                  <span>{item.duration ?? `全 ${item.seasons} 季 · ${item.episodes} 集`}</span>
                  <span>导演 / {item.director}</span>
                </>
              )}
            </div>

            {typeof item.rating === 'number' && item.rating > 0 && (
              <div className="mt-4 flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
                <div className="flex items-baseline gap-1">
                  <span className="font-heading text-3xl font-bold leading-none text-primary-600">
                    {item.rating.toFixed(1)}
                  </span>
                  <span className="font-label text-[11px] text-foreground-500">/ 10</span>
                </div>
                <div className="h-8 w-px bg-background-300"></div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i
                        key={star}
                        className={`ri-star-fill text-[12px] ${
                          star <= Math.round((item.rating ?? 0) / 2) ? 'text-accent-500' : 'text-background-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="mt-1 font-label text-[11px] text-foreground-500">编辑评分参考 · 示例数据</p>
                </div>
              </div>
            )}

            {item.type === 'news' && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                    favored
                      ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                      : 'border border-background-300 bg-background-50 text-foreground-800 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <i className={favored ? 'ri-heart-fill text-[16px]' : 'ri-heart-line text-[16px]'}></i>
                  {favored ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-share-forward-line text-[16px]"></i>
                  分享
                </button>
              </div>
            )}

            <div className="mt-6">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                {item.type === 'news' ? '正文' : '剧情简介'}
              </h2>
              <div className="mt-3 space-y-3">
                {paragraphs.map((paragraph, index) => (
                  <p
                    key={`${item.id}-p-${index}`}
                    className={`text-sm leading-[1.9] text-foreground-700 md:text-[15px] ${
                      item.type === 'news' && index === 0 ? 'font-medium text-foreground-900' : ''
                    }`}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {item.type !== 'news' && (
              <>
                <div className="mt-7">
                  <h2 className="font-heading text-base font-semibold text-foreground-950">主演阵容</h2>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(item.cast ?? []).map((person) => (
                      <span
                        key={person}
                        className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 py-1 pl-1 pr-3"
                      >
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                          <i className="ri-user-line text-[12px]"></i>
                        </span>
                        <span className="text-xs text-foreground-800">{person}</span>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-7">
                  <h2 className="font-heading text-base font-semibold text-foreground-950">影片参数</h2>
                  <dl className="mt-3 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-background-200 bg-background-200 md:grid-cols-3">
                    {[
                      { label: '类型', value: item.genre.join(' / ') },
                      { label: '地区', value: item.region },
                      { label: '上映年份', value: `${item.year} 年` },
                      item.type === 'movie'
                        ? { label: '片长', value: item.duration ?? '—' }
                        : { label: '季数 / 集数', value: `${item.seasons} 季 · ${item.episodes} 集` },
                      { label: '导演', value: item.director ?? '—' },
                      {
                        label: item.type === 'movie' ? '评分参考' : '播放状态',
                        value:
                          item.type === 'movie'
                            ? `${item.rating?.toFixed(1)} / 10`
                            : item.status ?? '—',
                      },
                    ].map((entry) => (
                      <div key={entry.label} className="bg-background-50 px-4 py-3">
                        <dt className="font-label text-[11px] text-foreground-500">{entry.label}</dt>
                        <dd className="mt-1 text-sm font-medium text-foreground-900">{entry.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </>
            )}

            {item.type === 'news' && (
              <div className="mt-7 rounded-lg border border-background-200 bg-background-100 p-4">
                <p className="font-label text-[11px] text-foreground-500">本文作者</p>
                <p className="mt-1 text-sm font-medium text-foreground-900">{item.author} · 玩偶姐姐编辑部</p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  内容为示例资讯，仅用于展示频道结构与阅读体验，不构成任何投资或购票建议。
                </p>
              </div>
            )}
          </div>
        </div>

        <section className="mt-12 border-t border-background-200 pt-8">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className="font-heading text-xl font-bold text-foreground-950">
              {item.type === 'news' ? '相关报道' : '你可能还想看'}
            </h2>
            <Link
              to={categoryLink}
              className="flex cursor-pointer items-center gap-1 font-label text-xs text-primary-600"
            >
              更多{categoryLabel}
              <i className="ri-arrow-right-line text-[12px]"></i>
            </Link>
          </div>
          {item.type === 'news' ? (
            <div className="mt-5 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {related.map((entry, index) => (
                <Link
                  key={entry.id}
                  to={`/detail/${entry.id}`}
                  className={`group flex cursor-pointer items-center gap-4 p-4 transition-colors hover:bg-background-100 ${
                    index !== related.length - 1 ? 'border-b border-background-200' : ''
                  }`}
                >
                  <div className="h-[76px] w-[118px] shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={entry.cover}
                      alt={`${entry.title} 配图`}
                      title={entry.title}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <span className="font-label text-[11px] text-foreground-400">
                      {entry.date} · {entry.articleCategory}
                    </span>
                    <h3 className="mt-1 line-clamp-2 font-heading text-sm font-semibold leading-relaxed text-foreground-950 transition-colors group-hover:text-primary-600">
                      {entry.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {related.map((entry) => (
                <ContentCard key={entry.id} item={entry} />
              ))}
            </div>
          )}
        </section>
      </article>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2">
          <div className="animate-fade-up flex items-center gap-2 rounded-full bg-foreground-950 px-4 py-2.5 text-xs text-background-50">
            <i className="ri-checkbox-circle-fill text-[15px] text-accent-500"></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}